import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Editor from "@monaco-editor/react";
import { RefreshCw, Hash, X } from "lucide-react";
import AdminLayout from "../../../layouts/AdminLayout";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import TextInput from "../../../components/ui/TextInput";
import Button from "../../../components/ui/Button";
import StatusBadge from "../../../components/ui/StatusBadge";
import Skeleton from "../../../components/ui/Skeleton";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import TablePagination from "../../../components/common/TablePagination";
import { adminGateEntryLogs } from "../../../services/inviteService";
import styles from "../../../styles/LogsPage.module.css";
import { DateRangePicker } from 'rsuite';
import { subDays, startOfMonth, endOfMonth, addDays } from 'date-fns';

const predefinedRanges = [
  { label: 'Today', value: [new Date(), new Date()] },
  { label: 'Yesterday', value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: 'Last 7 days', value: [subDays(new Date(), 6), new Date()] },
  { label: 'Last 30 days', value: [subDays(new Date(), 29), new Date()] },
  { label: 'This month', value: [startOfMonth(new Date()), new Date()] },
  {
    label: 'Last month',
    value: [
      startOfMonth(addDays(new Date(), -new Date().getDate())),
      endOfMonth(addDays(new Date(), -new Date().getDate()))
    ]
  },
];

const formatDateYyyyMmDd = (date) => {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTime = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  const yyyy = d.getFullYear();
  const MM = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${MM}-${dd} ${hh}:${mm}:${ss}`;
};

const AdminGateEntryLogs = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const [filters, setFilters] = useState({
    society_id: "",
    entry_type: "",
    entry_status: "",
    from: "",
    to: "",
  });

  const hasActiveFilters = useMemo(() => {
    return Boolean(filters.society_id || filters.entry_type || filters.entry_status || filters.from || filters.to);
  }, [filters]);

  const handleReset = useCallback(() => {
    setFilters({
      society_id: "",
      entry_type: "",
      entry_status: "",
      from: "",
      to: "",
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGateEntryLogs({
        society_id: filters.society_id || undefined,
        entry_type: filters.entry_type || undefined,
        entry_status: filters.entry_status || undefined,
        limit,
        offset,
      });
      setRows(res.logs || []);
      setTotal(Number(res.total ?? 0) || 0);
    } catch (error) {
      console.error("ADMIN GATE LOGS ERROR:", error);
      toast.error(error?.data?.message || error?.message || "Unable to load logs");
    } finally {
      setLoading(false);
    }
  }, [filters.entry_status, filters.entry_type, filters.society_id, limit, offset]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setOffset(0);
  }, [filters.entry_status, filters.entry_type, filters.society_id, limit]);

  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [limit, offset]);

  return (
    <AdminLayout title="Guard Logs">
      <div className={styles.filtersCard} style={{ flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ flex: 'none' }}>
          <DateRangePicker
            format="yyyy-MM-dd"
            placeholder="Select Date Range"
            value={filters.from && filters.to ? [new Date(filters.from), new Date(filters.to)] : null}
            onChange={(val) => {
              if (val && val.length === 2) {
                setFilters(p => ({ ...p, from: formatDateYyyyMmDd(val[0]), to: formatDateYyyyMmDd(val[1]) }));
              } else {
                setFilters(p => ({ ...p, from: "", to: "" }));
              }
            }}
            ranges={predefinedRanges}
            placement="bottomStart"
            style={{ width: 240 }}
          />
        </div>

        <div style={{ width: '160px', flex: 'none' }}>
          <TextInput
            placeholder="Society ID..."
            value={filters.society_id}
            onChange={(e) => setFilters((p) => ({ ...p, society_id: e.target.value }))}
            leadIcon={<Hash size={15} />}
          />
        </div>
        <div style={{ width: '160px', flex: 'none' }}>
          <SelectDropdown
            value={filters.entry_type}
            onChange={(e) => setFilters((p) => ({ ...p, entry_type: e.target.value }))}
            options={[
              { label: "All Types", value: "" },
              { label: "Guest", value: "guest" },
              { label: "Cab", value: "cab" },
              { label: "Delivery", value: "delivery" },
              { label: "Parcel", value: "parcel" },
              { label: "Service", value: "service" },
              { label: "Other", value: "other" }
            ]}
          />
        </div>
        <div style={{ width: '160px', flex: 'none' }}>
          <SelectDropdown
            value={filters.entry_status}
            onChange={(e) => setFilters((p) => ({ ...p, entry_status: e.target.value }))}
            options={[
              { label: "All Statuses", value: "" },
              { label: "Checked In", value: "checked_in" },
              { label: "Checked Out", value: "checked_out" },
              { label: "Denied", value: "denied" },
              { label: "Pending", value: "pending" }
            ]}
          />
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            title="Reset filters"
            style={hasActiveFilters ? { color: '#ef4444', borderColor: '#fca5a5', backgroundColor: '#fef2f2' } : {}}
          >
            <X size={15} style={{ marginRight: '4px' }} /> Reset
          </Button>
          <Button variant="outline" size="sm" onClick={load} title="Refresh logs">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} style={{ marginRight: '4px' }} /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.tableCard} style={{ padding: '24px' }}>
          <Skeleton variant="card" count={4} />
        </div>
      ) : (
        <>
          <div className={styles.tableCard}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.th}>ID</th>
                  <th className={styles.th}>Society</th>
                  <th className={styles.th}>Invite</th>
                  <th className={styles.th}>Guest</th>
                  <th className={styles.th}>Type</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Visitor</th>
                  <th className={styles.th}>Checked In</th>
                  <th className={styles.th}>Checked Out</th>
                  <th className={styles.th}>Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '32px' }}>
                      <EmptyState
                        title="No gate logs found"
                        description="There are no entry or exit events matching your current filter criteria."
                      />
                    </td>
                  </tr>
                ) : rows.map((r, idx) => {
                  const rowId = r.id || `row-${idx}`;
                  const isExpanded = expandedRow === rowId;
                  const rowClass = [styles.tbodyRow, isExpanded ? styles.activeRow : ""].filter(Boolean).join(" ");
                  return (
                    <React.Fragment key={rowId}>
                      <tr className={rowClass} onClick={() => toggleRow(rowId)}>
                        <td className={[styles.td, styles.nowrap].join(" ")}>
                          {r.original_id || r.id}
                          <br />
                          <span className={styles.metaText} style={{ fontSize: '10px' }}>{r.source}</span>
                        </td>
                        <td className={[styles.td, styles.nowrap].join(" ")}>{r.society_id}</td>
                        <td className={styles.td}>{r.invite_id || "-"}</td>
                        <td className={styles.td}>{r.invite_guest_id || "-"}</td>
                        <td className={[styles.td, styles.nowrap].join(" ")}>{r.entry_type}</td>
                        <td className={[styles.td, styles.nowrap].join(" ")}>
                          <StatusBadge status={r.entry_status} />
                        </td>
                        <td className={[styles.td, styles.wrap].join(" ")}>
                          {r.visitor_name || "-"}
                          {r.mobile_number ? (
                            <div className={styles.metaText}>{r.mobile_number}</div>
                          ) : null}
                        </td>
                        <td className={[styles.td, styles.nowrap, styles.mono].join(" ")}>
                          {formatDateTime(r.checked_in_at)}
                        </td>
                        <td className={[styles.td, styles.nowrap, styles.mono].join(" ")}>
                          {formatDateTime(r.checked_out_at)}
                        </td>
                        <td className={[styles.td, styles.nowrap, styles.mono].join(" ")}>
                          {formatDateTime(r.created_at)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} style={{ padding: 0 }}>
                            <div className={styles.expandedContainer}>
                              <div className={styles.expandedHeader}>
                                <div className={styles.expandedHeaderTitle}>
                                  <span className={styles.expandedHeaderTitleBold}>{String(r.entry_status || "INFO").toUpperCase()}</span>
                                  <span className={styles.expandedHeaderTitleSemi}>{r.entry_type || "Guard Log"}</span>
                                  • {formatDateTime(r.created_at)} (ID: {r.id || "N/A"})
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={styles.dismissBtn}
                                  onClick={(e) => { e.stopPropagation(); setExpandedRow(null); }}
                                >
                                  DISMISS VIEW
                                </Button>
                              </div>
                              <div className={styles.editorWrap}>
                                <Editor
                                  height="300px"
                                  defaultLanguage="json"
                                  theme="vs-dark"
                                  value={JSON.stringify(r, null, 2)}
                                  options={{ readOnly: true, minimap: { enabled: false }, wordWrap: "on" }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalItems={total}
            page={currentPage}
            limit={limit}
            onPageChange={(newPage) => setOffset((newPage - 1) * limit)}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setOffset(0);
            }}
            limitOptions={[5, 10, 25, 50, 100]}
          />
        </>
      )}
    </AdminLayout>
  );
};

export default AdminGateEntryLogs;
